import { format, differenceInDays, isPast } from 'date-fns'

export const formatDate = (date: string | Date) => {
  return format(new Date(date), 'MMM d, yyyy')
}

export const formatDateTime = (date: string | Date) => {
  return format(new Date(date), 'MMM d, yyyy h:mm a')
}

export const getRelativeTime = (date: string | Date) => {
  const days = differenceInDays(new Date(), new Date(date))
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`
  return `${Math.floor(days / 30)} months ago`
}

export const isOverdue = (date: string | Date): boolean => {
  return isPast(new Date(date))
}

export const truncateText = (text: string, maxLength: number = 50): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export const getInitials = (firstName: string, lastName?: string): string => {
  const first = firstName.charAt(0).toUpperCase()
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return (first + last).substring(0, 2)
}

export const getStatusColor = (status: string): string => {
  const statusColorMap: Record<string, string> = {
    'New': '#4bca81',
    'Working': '#4bca81',
    'Escalated': '#c23934',
    'Closed': '#54698d',
    'Converted': '#4bca81',
    'Nuevo': '#4bca81',
    'Contactado': '#ffb75d',
    'Nurturing': '#0070d2',
    'No cualificado': '#54698d',
    'Completed': '#4bca81',
    'In Progress': '#0070d2',
    'Not Started': '#dddbda',
  }
  return statusColorMap[status] || '#54698d'
}

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const capitalizeFirstLetter = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export const getPhoneDisplay = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

/**
 * Generates a valid 16-bit PCM Mono WAV file (8kHz) simulating telephone comfort noise/voices
 * for a given duration.
 */
export function generateCallRecordingWav(durationSeconds: number): Blob {
  const sampleRate = 8000;
  const numChannels = 1;
  const bitsPerSample = 16;
  const seconds = Math.max(1, durationSeconds);
  const numSamples = seconds * sampleRate;
  const subChunk2Size = numSamples * numChannels * (bitsPerSample / 8);
  const chunkSize = 36 + subChunk2Size;
  
  const buffer = new ArrayBuffer(44 + subChunk2Size);
  const view = new DataView(buffer);
  
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, chunkSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, subChunk2Size, true);
  
  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    // Simulating voices using modulated frequencies to make it sound like a telephone conversation
    const mod = Math.sin(2 * Math.PI * 0.15 * t);
    const speech = mod > 0
      ? Math.sin(2 * Math.PI * 220 * t) * 0.2 + Math.sin(2 * Math.PI * 440 * t) * 0.15 + Math.sin(2 * Math.PI * 880 * t) * 0.08
      : Math.sin(2 * Math.PI * 300 * t) * 0.15 + Math.sin(2 * Math.PI * 600 * t) * 0.1 + Math.sin(2 * Math.PI * 1200 * t) * 0.05;
    
    // Add some realistic comfort noise/static
    const staticNoise = (Math.random() - 0.5) * 0.02;
    const waveValue = speech * Math.abs(Math.sin(2 * Math.PI * 2 * t)) + staticNoise;
    
    const sample = Math.max(-1, Math.min(1, waveValue)) * 0x7FFF;
    view.setInt16(offset, sample, true);
    offset += 2;
  }
  
  return new Blob([buffer], { type: 'audio/wav' });
}
